import { Clear } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { parsePhoneNumber } from "libphonenumber-js";
import {
  cloneDeep,
  isEmpty,
  last,
  mapValues,
  omit,
  pick,
  set,
  startsWith,
} from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useCancelMapitShipOrderMutation,
  useCancelMapitShipShipmentMutation,
  useCreateMapitShipOrderMutation,
  useGetMapitShipOrdersByIdQuery,
  useLazyGetMapitShipOrderAWBQuery,
  useUpdateMapitShipOrderMutation,
  useForceCancelShipmentMutation,
} from "src/_api/mapitShipApi";
import { ConfirmDialog } from "src/_modules/common";
import CompleteProfileInfoAlert from "src/_modules/Account/components/CompleteProfileInfoAlert";
import { runJoiValidate, sanitizeShortAddress } from "src/_utils";
import { ENAAB_ID_MAPIT_SHIP, GOOGLE_STORAGE, ROLES } from "src/constants";
import { ORERR_SCHEMA } from "../../../../validationSchemas";
import ConfirmOrdersForShipmentDialog from "../ConfirmOrdersForShipmentDialog";
import ReorderCancelledShipmentDialog from "../ReorderCancelledShipmentDialog";
import AddOrderSection from "./AddOrderSection";
import EditOrderSection from "./EditOrderSection";
import ConfirmPasswordDialogAdmin from "src/_modules/common/Dialogs/ConfirmPasswordDialogAdmin";

const defaultOrder = {
  packagesCount: "",
  weight: "",
  amountPayable: "",
  warehouse: "",
  // paymentMethod: '',
  boxes: [{ dimensions: { length: "", width: "", height: "" }, weight: 1 }],
  orderItems: [
    {
      item: "",
      amount: "1",
      price: "",
      sku: "",
    },
  ],
  user: {
    name: "",
    phone: "",
    address: {
      city: "",
      state: "",
      area: "",
      street: "",
      houseNumber: "",
      landmark: "",
    },
  },
  sender: {
    name: "",
    phone: "",
  },
};

function OrderDialogContent({ open, orderId, onClose, isEditing, defaultBox }) {
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.entities.profile);
  const dialogRef = useRef();
  const accountAlertRef = useRef();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { data: order, isLoading } = useGetMapitShipOrdersByIdQuery(orderId, {
    skip: !orderId,
  });
  const enabId = ENAAB_ID_MAPIT_SHIP.includes(profile?.merchant?.id);

  const [tempOrder, setTempOrder] = useState({});
  console.log("temp order", tempOrder);

  const [validationErrors, setValidationsErrors] = useState(null);

  const handleOrderChange = ({ target: { name, value } }) => {
    setTempOrder((prevState) => {
      let payload = cloneDeep(prevState);
      set(payload, name, value);
      if (name === "boxes") {
        let totalWeight = value.reduce(
          (acc, box) => acc + Number(box.weight),
          0,
        );
        let totalBoxes = value.length;
        set(payload, "weight", totalWeight);
        set(payload, "packagesCount", totalBoxes);
      }
      return payload;
    });
    setValidationsErrors((prev) => omit(prev, [name]));
  };

  const getPhoneNumber = (phoneCode, phone) => {
    let phoneNumberString = phoneCode + phone;
    if (!startsWith(phoneCode, "+"))
      phoneNumberString = "+" + phoneNumberString;
    const phoneNumber = parsePhoneNumber(phoneNumberString, { extract: true });
    if (phoneNumber) return phoneNumber.format("E.164");
  };

  useEffect(() => {
    if (!!order && isEditing) {
      let payload = {
        boxes: order?.otoIntegration?.boxes,
        // paymentMethod: order?.otoIntegration?.paymentMethod,
        ...pick(cloneDeep(order), [
          "packagesCount",
          "weight",
          "amountPayable",
          "warehouse",
          "orderItems",
          "user",
          "sender",
        ]),
      };

      if (!!payload?.boxes?.length) {
        payload.boxes = payload.boxes.map((box) => omit(box, ["_id"]));
      }

      if (!!payload?.orderItems?.length) {
        payload.orderItems = payload.orderItems.map((orderItem) =>
          omit(orderItem, ["_id", "id"]),
        );
      }

      if (!!payload?.warehouse) {
        payload.warehouse = payload.warehouse?.id;
      }
    

      if (!!payload?.user?.phone) {
        payload.user.phone = getPhoneNumber(
          payload.user.phoneCode,
          payload.user.phone,
        );
      }

      if (!!payload?.boxes?.length) {
        let totalWeight = payload?.boxes?.reduce(
          (acc, box) => acc + Number(box.weight),
          0,
        );
        let totalBoxes = payload?.boxes?.length;
        set(payload, "weight", totalWeight);
        set(payload, "packagesCount", totalBoxes);
      }

      payload.boxes = payload?.boxes?.map((box) => {
        return {
          dimensions: {
            length: box.dimensions.length,
            width: box.dimensions.width,
            height: box.dimensions.height,
          },
          weight: box.weight,
        };
      });

      payload.orderItems = payload.orderItems.map((item) => {
        return {
          item: item.item,
          sku: item.sku,
          amount: item.amount,
          price: item.price,
        };
      });

      setTempOrder(payload);
    } else {
      if (!!defaultBox) {
        let payload = cloneDeep(defaultOrder);
        set(payload, "boxes", [
          {
            dimensions: { ...defaultBox.dimensions },
            weight: defaultBox.weight,
          },
        ]);
        set(payload, "weight", defaultBox.weight);
        set(payload, "packagesCount", 1);

        setTempOrder(payload);
      } else {
        setTempOrder(defaultOrder);
      }
    }
  }, [order, isEditing, defaultBox]);

  const [
    createOrder,
    {
      isLoading: createMapitShipLoading,
      isSuccess: createMapitShipIsSuccess,
      error: createMapitShipError,
      reset: resetCreateMapitShipOrder,
    },
  ] = useCreateMapitShipOrderMutation();

  const [
    updateOrder,
    {
      isLoading: updateMapitShipLoading,
      isSuccess: updateMapitShipIsSuccess,
      error: updateMapitShipError,
      reset: resetUpdateMapitShipOrder,
    },
  ] = useUpdateMapitShipOrderMutation();

  const onSubmit = async (e) => {
    try {
      e.preventDefault();
      setValidationsErrors(null);

      let payload = cloneDeep(tempOrder);

      if (!isEmpty(payload?.user?.location?.coordinates)) {
        // payload.user.location = payload.user.location.coordinates
        payload = set(payload, "user.location", [
          ...payload.user.location.coordinates,
        ]);
      } else {
        payload.user.location = [];
      }

      if (!!payload?.user?.phoneCode) {
        delete payload.user.phoneCode;
      }

      if (!!payload?.user?.address?.shortAddress) {
        payload.user.address.shortAddress = sanitizeShortAddress(
          payload.user.address.shortAddress,
        );
      }

      // Convert dimensions and weight to numbers
      payload.boxes = payload?.boxes?.map((box) => {
        box.dimensions = mapValues(box.dimensions, Number);
        box.weight = Number(box.weight);
        return box;
      });

      if (!enabId) {
        delete payload.sender;
      }
      if (!!payload?.sender?.otoWarehouseId) {
        delete payload?.sender?.otoWarehouseId;
      }

      const errors = runJoiValidate(ORERR_SCHEMA, payload);

      console.log("payload", payload);
      console.log("errors", errors);

      if (!!errors) {
        setValidationsErrors(errors);
        dialogRef.current.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const parsedPhone = parsePhoneNumber(payload.user.phone, {
        extract: true,
      });
      payload.user.phoneCode = `+${parsedPhone.countryCallingCode}`;
      payload.user.phone = parsedPhone.nationalNumber;
      payload.amountPayable = Number(payload.amountPayable);
      if (payload?.orderItems?.length > 0) {
        [...Array(payload.orderItems.length).keys()].forEach((index) => {
          payload.orderItems[index].price = Number(
            payload.orderItems[index].price,
          );
        });
      }
      if (isEditing) {
        await updateOrder({ id: order.id, payload }).unwrap();
      } else {
        await createOrder(payload).unwrap();
      }

      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      console.log("error catch", error);
    }
  };

  //======= Confirmation Dialog =======
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    confirmFn: undefined,
    title: "",
    content: "",
  });

  const resetConfirmationDialog = () => {
    setConfirmationDialog({
      open: false,
      confirmFn: undefined,
      title: "",
      content: "",
    });
  };

  //======= cancel Order =======
  const [
    cancelMapitShipOrder,
    {
      isLoading: cancelMapitShipOrderLoading,
      error: cancelMapitShipOrderError,
      isSuccess: cancelMapitShipOrderIsSuccess,
      reset: cancelMapitShipOrderOrderReset,
    },
  ] = useCancelMapitShipOrderMutation();

  const [
    cancelMapitShipShipment,
    {
      isLoading: cancelMapitShipShipmentLoading,
      error: cancelMapitShipShipmentError,
      isSuccess: cancelMapitShipShipmentIsSuccess,
      reset: cancelMapitShipShipmentOrderReset,
    },
  ] = useCancelMapitShipShipmentMutation();

  const cancelHandler = async () => {
    try {
      resetConfirmationDialog();
      if (order?.currentStatus === "ORDER_PENDING") {
        await cancelMapitShipOrder(order.id).unwrap();
      } else {
        const shipmentId =
          order?.otoIntegration?.returnShipment?.id ??
          order?.otoIntegration?.shipment?.id;
        await cancelMapitShipShipment({ shipmentId }).unwrap();
      }
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) { }
  };

  // The create-order endpoint returns { status, message }; the others return a plain message.
  const accountNotActivated = createMapitShipError?.status === 648;

  const error =
    createMapitShipError?.message ??
    updateMapitShipError ??
    cancelMapitShipOrderError ??
    cancelMapitShipShipmentError;
  const isSuccess =
    createMapitShipIsSuccess ||
    updateMapitShipIsSuccess ||
    cancelMapitShipOrderIsSuccess ||
    cancelMapitShipShipmentIsSuccess;
  const loading =
    createMapitShipLoading ||
    updateMapitShipLoading ||
    cancelMapitShipOrderLoading ||
    cancelMapitShipShipmentLoading;

  useEffect(() => {
    if (accountNotActivated) {
      accountAlertRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [accountNotActivated]);

  useEffect(() => {
    if (!open) {
      setTempOrder(defaultOrder);
      setValidationsErrors(null);
      resetCreateMapitShipOrder();
      resetUpdateMapitShipOrder();
      cancelMapitShipOrderOrderReset();
      cancelMapitShipShipmentOrderReset();
      setValidationsErrors(null);
      resetConfirmationDialog();
    }
  }, [open]);

  //======= Order AWB =======
  const [getMapitShipOrderAWB, { isLoading: awbLoading }] =
    useLazyGetMapitShipOrderAWBQuery();

  const openAWBHandler = async () => {
    try {
      if (!!order?.otoIntegration?.shipment?.AWB) {
        if (order?.otoIntegration?.shipment?.AWB.startsWith("https://")) {
          window.open(
            order?.otoIntegration?.shipment?.AWB,
            "_blank",
          );
        } else {
          window.open(
            `${GOOGLE_STORAGE}/${order?.otoIntegration?.shipment?.AWB}`,
            "_blank",
          );
        }
      } else {
        let response = await getMapitShipOrderAWB(orderId).unwrap();
        if (!!response?.AWB) {
          window.open(`${GOOGLE_STORAGE}/${response?.AWB}`, "_blank");
        }
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const openReturnAWBHandler = async () => {
    try {
      if (order?.otoIntegration?.returnShipment?.AWB.startsWith("https://")) {
        window.open(
          order?.otoIntegration?.returnShipment?.AWB,
          "_blank",
        );
      } else {
        window.open(
          `${GOOGLE_STORAGE}/${order?.otoIntegration?.returnShipment?.AWB}`,
          "_blank",
        );
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const confirmCancelHandler = () => {
    setConfirmationDialog({
      open: true,
      confirmFn: cancelHandler,
      title: "Warning",
      content:
        order?.currentStatus === "ORDER_PENDING"
          ? "cancelOrderAlert"
          : "cancelShipmentAlert",
    });
  };

  const openTrackingURLHandler = () => {
    if (!!order?.otoIntegration?.returnShipment) {
      window.open(order?.otoIntegration?.returnShipment?.trackingUrl, "_blank");
    } else {
      window.open(order?.otoIntegration?.shipment?.trackingUrl, "_blank");
    }
  };

  //========  Open shipment dialog for create return shipment =======
  const [openCreateShipmentDialog, setOpenCreateShipmentDialog] =
    useState(false);

  const openOrderShipmentDialogHandler = () => {
    setOpenCreateShipmentDialog(true);
  };

  const closeOrdersShipmentsDialogHandler = () => {
    setOpenCreateShipmentDialog(false);
  };

  const showCancelShipment = () => {
    return ["ORDER_PENDING", "ORDER_ASSIGNED_TO_SHIPPING_COMPANY"].includes(
      order?.currentStatus,
    );
  };

  //========  Open shipment dialog for reorder shipment =======

  const [openReorderOrdertDialog, setOpenReorderOrdertDialog] = useState(false);

  const closeReorderOrdertDialogHandler = () => {
    setOpenReorderOrdertDialog(false);
  };

  const openReOrderDialogHandler = () => {
    setOpenReorderOrdertDialog(true);
  };
  const [openAdminDialog, setOpenAdminDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");

  const [
    forceCancelShipment,
    {
      isLoading: forceCancelShipmentLoading,
      isSuccess: forceCancelShipmentIsSuccess,
      error: forceCancelShipmentError,
    },
  ] = useForceCancelShipmentMutation();

  const submitForceCancelHandler = async () => {
    try {
      const shipmentId = order?.otoIntegration?.shipment?.id;

      await forceCancelShipment({
        shipmentId,
        password: adminPassword,
      }).unwrap();

      setOpenAdminDialog(false);
      setAdminPassword("");

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.log(error);
    }
  };

  const showForceCancelButton =
    profile?.role === ROLES.admin &&
    cancelMapitShipShipmentError &&
    order?.otoIntegration?.shipment?.deliveryOption?.integrationType ===
    "aramex";
  return (
    <>
      {openCreateShipmentDialog && (
        <ConfirmOrdersForShipmentDialog
          open={openCreateShipmentDialog}
          onClose={closeOrdersShipmentsDialogHandler}
          selectedOrders={[order]}
          shipmentType="RETURN"
        />
      )}
      <ConfirmDialog
        open={confirmationDialog?.open}
        onClose={() => resetConfirmationDialog()}
        confirmFn={confirmationDialog?.confirmFn}
        title={confirmationDialog?.title}
        content={confirmationDialog?.content}
      />
      <ReorderCancelledShipmentDialog
        open={openReorderOrdertDialog}
        onClose={closeReorderOrdertDialogHandler}
        orderId={order?.id}
      />
      <Dialog
        open={open}
        onClose={(e) => e.preventDefault()}
        maxWidth="lg"
        dir={theme.direction}
        fullScreen={fullScreen}
        fullWidth
        PaperProps={{
          ref: dialogRef,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50%",
              minHeight: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
              {isEditing
                ? `${t("Edit order")} ${order?.orderNumber ?? ""}`
                : t("Create order")}
              <IconButton sx={{ ml: "auto" }} onClick={onClose} color="primary">
                <Clear />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
              {isEditing ? (
                <EditOrderSection
                  order={order}
                  tempOrder={tempOrder}
                  handleOrderChange={handleOrderChange}
                  validationErrors={validationErrors}
                  disableEditing={
                    !(
                      profile?.role !== ROLES.admin &&
                      (order?.currentStatus === "ORDER_PENDING" || !isEditing)
                    )
                  }
                />
              ) : (
                <AddOrderSection
                  tempOrder={tempOrder}
                  handleOrderChange={handleOrderChange}
                  validationErrors={validationErrors}
                />
              )}
              {accountNotActivated && (
                <Box ref={accountAlertRef}>
                  <CompleteProfileInfoAlert
                    variant="outlined"
                    forceOpen
                    dismissible={false}
                    fallbackMessage={createMapitShipError?.message}
                    sx={{ mx: 0, mt: 2, mb: 0 }}
                  />
                </Box>
              )}
            </DialogContent>
            <Divider />
            <DialogActions>
              <LoadingButton
                variant="contained"
                sx={{ textTransform: "none" }}
                type="button"
                onClick={onClose}
                loading={loading}
              >
                {t("Cancel")}
              </LoadingButton>
              <Box mr="auto" />
              {isSuccess && (
                <Typography color="success.main">
                  {t("Done successfully")}
                </Typography>
              )}
              {!!error && !accountNotActivated && (
                <Typography color="error">
                  {showForceCancelButton
                    ? t(
                      "Shipment cannot be canceled normally, please use force cancel",
                    )
                    : error}
                </Typography>
              )}
              <Box mr="auto" />
              {order?.otoIntegration?.shipment?.currentStatus !==
                "shipmentCanceled" &&
                isEditing && (
                  <>
                    {(!!order?.otoIntegration?.shipment?.trackingUrl ||
                      !!order?.otoIntegration?.returnShipment?.trackingUrl) && (
                        <LoadingButton
                          variant="contained"
                          sx={{ textTransform: "none" }}
                          onClick={openTrackingURLHandler}
                        >
                          {t("Track Shipment")}
                        </LoadingButton>
                      )}
                    {!!order?.otoIntegration?.shipment?.deliveryOption &&
                      ![
                        "ORDER_PENDING",
                        "ORDER_CANCELED",
                        "ORDER_COMPLETED",
                        "ORDER_COMPLETED",
                        "ORDER_RETURN",
                      ].includes(order?.currentStatus) &&
                      !order?.otoIntegration?.returnShipment?.AWB && (
                        <LoadingButton
                          id="mapitShip-download-awb-btn"
                          variant="contained"
                          sx={{ textTransform: "none" }}
                          type="button"
                          onClick={openAWBHandler}
                          loading={loading || awbLoading}
                        >
                          {t("AWB")}
                        </LoadingButton>
                      )}
                    {!!order?.otoIntegration?.returnShipment?.AWB && (
                      <LoadingButton
                        variant="contained"
                        sx={{ textTransform: "none" }}
                        type="button"
                        onClick={openReturnAWBHandler}
                        loading={loading || awbLoading}
                      >
                        {t("Return AWB")}
                      </LoadingButton>
                    )}
                  </>
                )}

              {["ORDER_COMPLETED"].includes(order?.currentStatus) &&
                order?.otoIntegration?.returnShipment?.currentStatus !==
                "pending" && (
                  <LoadingButton
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    type="button"
                    onClick={() => openOrderShipmentDialogHandler()}
                    loading={loading}
                  >
                    {t("Create Return Shipment")}
                  </LoadingButton>
                )}
              {profile?.role !== ROLES.admin && isEditing && (
                <>
                  {order?.currentStatus === "ORDER_CANCELED" && (
                    <LoadingButton
                      variant="contained"
                      sx={{ textTransform: "none" }}
                      type="button"
                      onClick={openReOrderDialogHandler}
                      loading={loading}
                    >
                      {t("Reorder")}
                    </LoadingButton>
                  )}
                </>
              )}
              {showCancelShipment() &&
                isEditing &&
                !(profile?.role === ROLES.admin && showForceCancelButton) && (
                  <LoadingButton
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    color="error"
                    disabled={showForceCancelButton}
                    type="button"
                    onClick={() => confirmCancelHandler()}
                    loading={loading}
                  >
                    {order?.currentStatus === "ORDER_PENDING"
                      ? t("Cancel order")
                      : t("Cancel shipment")}
                  </LoadingButton>
                )}

              {showForceCancelButton && (
                <LoadingButton
                  variant="contained"
                  sx={{ textTransform: "none" }}
                  color="error"
                  type="button"
                  onClick={() => setOpenAdminDialog(true)}
                  loading={forceCancelShipmentLoading}
                >
                  {t("Force Cancel Shipment")}
                </LoadingButton>
              )}

              <ConfirmPasswordDialogAdmin
                open={openAdminDialog}
                error={forceCancelShipmentError}
                isSuccess={forceCancelShipmentIsSuccess}
                title={
                  "Please enter your password to be able to cancel shipment"
                }
                password={adminPassword}
                loading={forceCancelShipmentLoading}
                onPasswordChange={setAdminPassword}
                onClose={() => {
                  setOpenAdminDialog(false);
                  setAdminPassword("");
                }}
                onConfirm={() => {
                  submitForceCancelHandler();
                }}
              />
              {profile?.role !== ROLES.admin &&
                (order?.currentStatus === "ORDER_PENDING" || !isEditing) && (
                  <LoadingButton
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    type="submit"
                    loading={loading}
                    disabled={!isEditing && createMapitShipIsSuccess}
                  >
                    {t("Confirm")}
                  </LoadingButton>
                )}
            </DialogActions>
          </form>
        )}
      </Dialog>
    </>
  );
}

class OrderDialogErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message ?? "Unknown error" };
  }

  componentDidCatch(error, errorInfo) {
    console.error("OrderDialog crashed", error, errorInfo);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.hasError && !prevState.hasError) {
      window.alert(`Order dialog error: ${this.state.errorMessage}`);
    }

    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, errorMessage: "" });
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export default function OrderDialog(props) {
  return (
    <OrderDialogErrorBoundary resetKey={`${props.open}-${props.orderId ?? ""}`}>
      <OrderDialogContent {...props} />
    </OrderDialogErrorBoundary>
  );
}
